<template>
  <div class="login auth-page" id="auth" :style="authStyle" v-if="!showLoader">
    <div class="container" v-if="company">
      <div class="row">
        <div class="col-12">
          <div class="card card-container mt-5" v-if="!hideLogin">
            <h2>{{ 'login.loginToYourAccount' | translate }}</h2>
            <form class="form-signin" @submit.prevent="login">
              <span id="reauth-email" class="reauth-email"></span>
              <input
                name="email"
                type="email"
                autocomplete="email"
                id="inputEmail"
                class="form-control"
                :placeholder="$t('login.enterEmail')"
                required
                autofocus
                v-model="user.email"
                aria-label="email"
                :disabled="stepTwoLogin"
              />
              <div v-if="stepTwoLogin" class="backStep1">
                <a href="#" @click="changeEmail">{{ $t('login.changeEmail') }}</a>
              </div>
              <input
                name="password"
                type="password"
                autocomplete="password"
                id="inputPassword"
                class="form-control mt-3"
                :placeholder="$t('form.passwordPlaceholder')"
                required
                v-model="user.password"
                aria-label="password"
                v-show="stepTwoLogin"
              />
              <select
                autocomplete="nope"
                class="form-control mt-3"
                name="language-select"
                id="language-select"
                v-model="user.language"
                @change="updateLanguage"
                v-if="company && company.languages.length > 1"
                aria-label="language"
              >
                <option :value="null" disabled>{{ 'settings.changeYourLanguage' | translate }}</option>
                <option v-for="language in company.languages" :value="language.localeCode" :key="language.languageId">
                  {{ language.languageName }}
                </option>
              </select>
              <br />

              <button
                class="btn btn-primary btn-block btn-signin btn-lg"
                v-if="!stepTwoLogin && !showLoginOptions"
                @click="next"
              >
                {{ $t('ui.next') }}
              </button>
              <div class="mt-3">
                <primary-button
                  v-if="isTalentechSynced"
                  size="large"
                  type="button"
                  class="btn-block btn-signin mb-2"
                  @click="redirectToTalentechLogin"
                  >{{ 'login.loginWithTalentechId' | translate }}</primary-button
                >
                <primary-button
                  v-if="isCandidateIdSynced"
                  size="large"
                  type="button"
                  class="btn-block btn-signin mb-2"
                  @click.prevent="redirectToCandidateLogin"
                  >{{ 'login.loginWithCandidateId' | translate }}</primary-button
                >
              </div>
              <primary-button
                v-if="stepTwoLogin"
                :loading="loading"
                size="large"
                type="submit"
                class="btn-block btn-signin"
                >{{ 'login.login' | translate }}</primary-button
              >
            </form>
            <br />
            <router-link v-if="stepTwoLogin" to="forgot-password" class="auth-link float-left">{{
              'login.forgotYourPassword' | translate
            }}</router-link>
            <div
              :class="{ 'mt-2': true, 'd-none': this.company.hidePrivacyPolicyConsentOnLoginPages === true }"
              v-html="loginFooterText"
            ></div>
          </div>
        </div>
      </div>

      <div v-if="loading && hideLogin" class="loader-container">
        <loader></loader>
      </div>
      <sweet-modal :title="$t('login.chooseApp')" :blocking="true" width="400" ref="template-modal" id="template-modal">
        <div class="modal-body">
          <div class="row">
            <div
              class="col-md-4 text-center pointer"
              v-for="onboarding in onboardings"
              :key="onboarding.id"
              @click="selectOnboarding(onboarding)"
            >
              <img
                v-if="onboarding.template.imageURL"
                :src="onboarding.template.imageURL"
                :alt="onboarding.template.name"
                class="img-fluid"
                width="100%"
                height="auto"
              />
              <img
                v-else
                src="../../../shared/assets/img/app-placeholder.png"
                :alt="onboarding.template.name"
                class="img-fluid"
                width="100%"
                height="auto"
              />
              <h3>{{ onboarding.template.name }}</h3>
            </div>
          </div>
        </div>
        <div slot="modal-footer" class="modal-footer"></div>
      </sweet-modal>
    </div>
  </div>
  <div class="pt-24" v-else>
    <loader></loader>
  </div>
</template>

<script>
import authMixin from '../../mixins/auth';
import { mapGetters } from 'vuex';
import { SweetModal } from 'sweet-modal-vue';
import PrimaryButton from '@shared/components/global/PrimaryButton.vue';
import { checkSyncedStatus } from '@app/http/authentication';
import { updateLanguage } from '@shared/translations';
const { VUE_APP_TALENTECH_AUTH, VUE_APP_CANDIDATE_AUTH } = process.env;

export default {
  components: {
    PrimaryButton,
    SweetModal
  },
  mixins: [authMixin],
  mounted() {
    if (this.$route.query.expired) {
      this.$notify({
        type: 'error',
        message: 'Session Expired',
        position: 'bottom-right'
      });
    }
  },
  created() {
    let talentechIDToken = this.$route.query['tid-token'];
    if (talentechIDToken) {
      this.hideLogin = true;
      this.loading = true;
      talentechIDToken = atob(talentechIDToken);
      this.loginWithTalentechToken(talentechIDToken);
      return;
    }

    let candidateIdToken = this.$route.query['cid-token'];
    if (candidateIdToken) {
      console.log('cid-token exchange started');
      console.log(candidateIdToken);
      this.hideLogin = true;
      this.loading = true;
      candidateIdToken = atob(candidateIdToken);
      this.loginWithCandidateIdToken(candidateIdToken);
      return;
    }

    const { email, password, authenticatorToken } = this.$store.state.auth.twoFactorSetup;
    if (email && password && authenticatorToken) {
      this.hideLogin = true;
      this.loading = true;
      this.user.email = email;
      this.user.password = password;

      const user = {
        ...this.user,
        subdomain: this.$store.getters['settings/subdomain']
      };

      this.$store.dispatch('auth/setTwoFactorSetup', {
        token: null,
        email: null,
        password: null,
        authenticatorToken: null
      });
      this.$http.post('auth', user, {}).then((response) => {
        this.twoFactorLogin(authenticatorToken, response.data.token);
      });
    } else if (email && password) {
      this.user.email = email;
      this.user.password = password;
      this.$store.dispatch('auth/setTwoFactorSetup', {
        token: null,
        email: null,
        password: null,
        authenticatorToken: null
      });
      this.login();
    }
  },
  data() {
    return {
      showLoader: false,
      loading: false,
      stepTwoLogin: false,
      showLoginOptions: false,
      isTalentechSynced: false,
      isCandidateIdSynced: false
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin']),
    ...mapGetters('settings', ['subdomain']),
    loginFooterText() {
      let footerText = this.$t('login.agreeToTerms');
      if (this.company.licenseAgreementURL) {
        footerText = footerText.replace(
          '{termsAndConditions}',
          '<a style="color: #444444;text-decoration: underline;" href="' +
            this.company.licenseAgreementURL +
            '" target="_blank">' +
            this.$t('general.termsAndConditions') +
            '</a>'
        );
        footerText = footerText.replace(
          '{privacyPolicy}',
          '<a style="color: #444444;text-decoration: underline;" href="/privacy-policy" target="_blank">' +
            this.$t('footer.privacyPolicy') +
            '</a>'
        );
      } else {
        footerText = footerText.replace('{termsAndConditions}', this.$t('general.termsAndConditions'));
        footerText = footerText.replace(
          '{privacyPolicy}',
          '<a style="color: #444444;text-decoration: underline;" href="/privacy-policy" target="_blank">' +
            this.$t('footer.privacyPolicy') +
            '</a>'
        );
      }
      return footerText;
    }
  },
  methods: {
    updateLanguage() {
      const languageId = this.company.languages.find((language) => language.localeCode === this.user.language)
        .languageId;

      if (window.localStorage.getItem('onboarding_user') !== null) {
        let user = JSON.parse(window.localStorage.getItem('onboarding_user'));
        user.language = this.user.language;
        user.languageId = languageId;
        window.localStorage.setItem('onboarding_user', JSON.stringify(user));
      } else {
        const user = {
          language: this.user.language,
          languageId
        };
        window.localStorage.setItem('onboarding_user', JSON.stringify(user));
      }
      window.localStorage.setItem('onboarding_defaultLanguage', this.user.language);
      updateLanguage(this.user.language);
    },
    next(event) {
      event.preventDefault();
      if (this.user.email) {
        checkSyncedStatus(this.company.id, this.user.email).then((response) => {
          const synced = response.data.synced;
          const candidateIdSynced = response.data.candidateIdSynced;
          this.isTalentechSynced = synced || false;
          this.isCandidateIdSynced = candidateIdSynced || false;

          if (!synced) {
            if (!candidateIdSynced) {
              this.stepTwoLogin = true;
              this.focusField('#inputPassword');
            } else {
              this.showLoginOptions = true;
              this.stepTwoLogin = false;
            }
          } else {
            this.showLoginOptions = true;
            this.stepTwoLogin = false;
            if (!candidateIdSynced) {
              this.redirectToTalentechLogin();
            }
          }
        });
      }
    },
    changeEmail(e) {
      e.preventDefault();
      this.user.password = '';
      this.stepTwoLogin = false;
      this.showLoginOptions = false;
      this.focusField('#inputEmail');
    },
    redirectToTalentechLogin() {
      const talentechAuthUrl = `${VUE_APP_TALENTECH_AUTH}?url=${window.location.host +
        window.location.pathname}&subdomain=${this.subdomain}&email=${encodeURIComponent(this.user.email)}`;
      window.location.href = talentechAuthUrl;
    },
    redirectToCandidateLogin() {
      if (!VUE_APP_CANDIDATE_AUTH) {
        console.error('VUE_APP_CANDIDATE_AUTH is not defined');
        this.showErrorNotification('Candidate authentication URL is not configured');
        return;
      }
      const candidateAuthUrl = `${VUE_APP_CANDIDATE_AUTH}?url=${window.location.host +
        window.location.pathname}&subdomain=${this.subdomain}&email=${encodeURIComponent(this.user.email)}`;
      window.location.href = candidateAuthUrl;
    },
    focusField(query) {
      setTimeout(() => {
        document.querySelector(query).focus();
      }, 10);
    }
  }
};
</script>

<style lang="scss" scoped>
@import './auth.scss';
</style>
